/**
 * ZonePanel — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.zonePanel,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';

export function create(hud) {
  const container = new PIXI.Container();
  container.visible = false;

  const bg = new PIXI.Graphics();
  container.addChild(bg);

  const phaseText = hud._createText('Zone Phase 1/1', 16, 0x72ffb8, true);
  phaseText.anchor.set(0.5, 0.5);
  container.addChild(phaseText);

  const timerText = hud._createText('Shrinks in 0:00', 20, 0xffffff, true);
  timerText.anchor.set(0.5, 0.5);
  container.addChild(timerText);

  const warningText = hud._createText('Taking zone damage!', 14, 0xff6161, true);
  warningText.anchor.set(0.5, 0.5);
  warningText.visible = false;
  container.addChild(warningText);

  hud.hudContainer.addChild(container);

  hud.elements.zonePanel = {
    container,
    bg,
    phaseText,
    timerText,
    warningText,
    width: 280,
    height: 80
  };
  hud._positionZonePanel();
}

export function position(hud) {
  const panel = hud.elements.zonePanel;
  if (!panel) return;

  const x = (hud.screenWidth - panel.width) / 2;
  const y = 20;
  panel.container.x = x;
  panel.container.y = y;

  panel.phaseText.x = panel.width / 2;
  panel.phaseText.y = 24;

  panel.timerText.x = panel.width / 2;
  panel.timerText.y = 48;

  panel.warningText.x = panel.width / 2;
  panel.warningText.y = 68;

  panel.bg.clear();
  panel.bg.alpha = 1.0;
  panel.bg.setStrokeStyle({ width: 2, color: 0x72ffb8 });
  panel.bg.roundRect(0, 0, panel.width, panel.height, 10);
  panel.bg.fill({ color: 0x000000 });
  panel.bg.stroke();

  // Set container alpha for transparency effect
  panel.container.alpha = 0.95;
}

export function update(hud, gameState) {
  const panel = hud.elements.zonePanel;
  if (!panel) return;

  const zoneState = gameState.zoneState;
  const visible = Boolean(zoneState && zoneState.shape);
  panel.container.visible = visible;

  if (!visible) return;

  const view = typeof gameState.zoneView === 'function' ? gameState.zoneView() : null;
  const phaseIndex = Math.max(0, (zoneState.stageIndex ?? zoneState.phaseIndex ?? 0)) + 1;
  const totalPhases = zoneState.totalPhases || '?';
  const dmg = zoneState.damagePerTick ?? 0;
  const next = zoneState.nextDamagePerTick;
  panel.phaseText.text = `Storm ${phaseIndex}/${totalPhases} · ${dmg} dmg/tick${next != null && next !== dmg ? ` → ${next}` : ''}`;

  const remaining = view?.timeRemaining ?? zoneState.timeRemaining ?? 0;
  const phase = view?.phase ?? zoneState.phase;
  if (phase === 'closed') {
    panel.timerText.text = 'Storm closed';
  } else if (phase === 'shrink') {
    panel.timerText.text = `Storm moving · ${hud._formatZoneTimer(remaining)}`;
  } else if (remaining > 0) {
    panel.timerText.text = `Storm moves in ${hud._formatZoneTimer(remaining)}`;
  } else {
    panel.timerText.text = 'Storm moving';
  }

  const takingDamage = hud._isTakingZoneDamage(gameState);
  panel.warningText.visible = takingDamage;
  if (takingDamage) {
    panel.warningText.text = 'Taking zone damage!';
  }
}
